import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  CoveragePreview,
  RegionInput,
  RegionMutationResult,
  ServiceRegionList,
  ServiceabilityCheck,
} from './types';

const KEY = ['service-regions'];

export function use_service_regions() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<ServiceRegionList>('/admin/service-regions')).data,
  });
}

/**
 * Every mutation invalidates the vendor lists too: changing a region changes
 * which vendors customers can see, and a stale count on screen is the kind of
 * thing an admin would act on.
 */
function on_region_change(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: KEY });
  void qc.invalidateQueries({ queryKey: ['vendors'] });
}

export function use_create_region() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RegionInput) =>
      (await api.post<RegionMutationResult>('/admin/service-regions', input)).data,
    onSuccess: () => on_region_change(qc),
  });
}

export function use_update_region() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<RegionInput> & { id: string }) =>
      (await api.patch<RegionMutationResult>(`/admin/service-regions/${id}`, input)).data,
    onSuccess: () => on_region_change(qc),
  });
}

export function use_delete_region() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/service-regions/${id}`)).data,
    onSuccess: () => on_region_change(qc),
  });
}

/** "What would this cover?" — read-only, changes nothing. */
export function use_preview_coverage() {
  return useMutation({
    mutationFn: async (input: { centre_lat: number; centre_lng: number; radius_km: number }) =>
      (await api.post<CoveragePreview>('/admin/service-regions/preview', input)).data,
  });
}

/** "Would a customer standing here be served?" */
export function use_check_point() {
  return useMutation({
    mutationFn: async (input: { lat: number; lng: number }) =>
      (
        await api.get<ServiceabilityCheck>('/admin/service-regions/check', {
          params: { lat: input.lat, lng: input.lng },
        })
      ).data,
  });
}
